import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Fingerprint, Smartphone, Tablet, Monitor, Laptop,
  Trash2, Pencil, Check, X, Loader2, Plus, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL as API } from '@/utils/apiConfig';
import { isPlatformAuthenticatorAvailable, registerBiometric } from '@/utils/webauthn';
import {
  isIosDespiaRuntime,
  triggerDespiaBioauth,
  generateDeviceSecret,
  saveDeviceSecret,
  warmupDespiaBridge,
} from '@/utils/despiaBioauth';
import CollapsibleSettingsCard from '@/components/CollapsibleSettingsCard';

import { useTranslation } from 'react-i18next';

// Same per-device flag the vault hook uses, so the "this device" badge stays in sync
const DEVICE_ENROLLED_KEY = (userId) => `vault_device_biometric_${userId || 'anon'}`;

const PLATFORM_ICON = {
  ios: Smartphone,
  android: Smartphone,
  macos: Laptop,
  windows: Monitor,
  linux: Monitor,
  other: Tablet,
};

const PLATFORM_LABEL = {
  ios: 'iOS',
  android: 'Android',
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
  other: 'Device',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatRelative(iso) {
  if (!iso) return 'Never used';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(iso);
  } catch {
    return '—';
  }
}

export default function BiometricDevicesPanel({ glassCard, onChange }) {
  const {
    t
  } = useTranslation();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [biometricCapable, setBiometricCapable] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, devRes] = await Promise.all([
        axios.get(`${API}/vault/status`, { withCredentials: true }),
        axios.get(`${API}/vault/webauthn/devices`, { withCredentials: true }),
      ]);
      setHasPin(!!statusRes.data?.has_pin);
      setCurrentUserId(statusRes.data?.user_id || null);
      setDevices(devRes.data?.devices || []);
    } catch {
      // No vault yet, or not authenticated — hide gracefully
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // iOS Despia uses the native bioauth bridge (WKWebView blocks WebAuthn).
    // Android Despia and web run the standard WebAuthn capability probe —
    // Despia Android's Chromium WebView exposes navigator.credentials.
    //
    // IMPORTANT: We intentionally *only gate the button off* when we're
    // certain there is no biometric path (no WebAuthn API at all AND not
    // inside iOS Despia). `isUserVerifyingPlatformAuthenticatorAvailable()`
    // returns false in several edge cases (Android Chrome incognito, some
    // WebView builds, iOS Safari with no enrolled Face/Touch ID) where the
    // user *can* still enroll if they retry after configuring biometrics —
    // and previously this returned-false probe was hiding the button
    // entirely. We now surface the button when WebAuthn exists at all and
    // let the actual create() call throw with a real error the user can
    // act on (e.g. "set up Face ID in Settings").
    if (isIosDespiaRuntime()) {
      setBiometricCapable(true);
      warmupDespiaBridge();
    } else if (typeof window !== 'undefined' && window.PublicKeyCredential) {
      setBiometricCapable(true);
      // Best-effort: also run the probe and log; don't gate UI on it.
      isPlatformAuthenticatorAvailable().then((ok) => {
        if (!ok) console.warn('[biometric] platform authenticator probe returned false — button still shown so user can attempt enrollment and see the real error');
      });
    } else {
      setBiometricCapable(false);
    }
    fetchDevices();
  }, [fetchDevices]);

  const startRename = (device) => {
    setEditingId(device.credential_id);
    setEditingLabel(device.device_label || '');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingLabel('');
  };

  const saveRename = async (device) => {
    const newLabel = editingLabel.trim();
    if (!newLabel || newLabel === device.device_label) {
      cancelRename();
      return;
    }
    setSavingId(device.credential_id);
    try {
      await axios.patch(
        `${API}/vault/webauthn/devices/${encodeURIComponent(device.credential_id)}`,
        { device_label: newLabel },
        { withCredentials: true },
      );
      setDevices((prev) =>
        prev.map((d) =>
          d.credential_id === device.credential_id ? { ...d, device_label: newLabel } : d,
        ),
      );
      toast.success(t('generated.biometricdevicespanel_device_renamed_5574'));
      cancelRename();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to rename');
    } finally {
      setSavingId(null);
    }
  };

  const removeDevice = async (device) => {
    const isThisDevice =
      currentUserId && localStorage.getItem(DEVICE_ENROLLED_KEY(currentUserId)) === '1';
    const confirmMsg = isThisDevice
      ? `Remove "${device.device_label}"? You'll need to re-enroll biometric on this device after removal.`
      : `Remove "${device.device_label}"? That device will lose biometric access (PIN still works).`;
    if (!window.confirm(confirmMsg)) return;
    setSavingId(device.credential_id);
    try {
      const res = await axios.delete(
        `${API}/vault/webauthn/devices/${encodeURIComponent(device.credential_id)}`,
        { withCredentials: true },
      );
      setDevices((prev) => prev.filter((d) => d.credential_id !== device.credential_id));
      // If we removed the credential matching THIS device, clear the local flag
      // so the user gets re-prompted to enroll on next vault unlock.
      if (res.data?.remaining === 0 || isThisDevice) {
        try {
          localStorage.removeItem(DEVICE_ENROLLED_KEY(currentUserId));
        } catch {}
      }
      toast.success(t('generated.biometricdevicespanel_device_removed_1c27'));
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    } finally {
      setSavingId(null);
    }
  };

  const enrollThisDevice = async () => {
    if (!biometricCapable || !hasPin) return;
    setEnrolling(true);
    try {
      console.log('[biometric] enroll start', {
        isIosDespia: isIosDespiaRuntime(),
        ua: navigator.userAgent,
        origin: window.location.origin,
      });
      if (isIosDespiaRuntime()) {
        // Native Face ID / Touch ID inside Despia iOS only — WebAuthn is
        // not exposed in iOS WKWebView. Android Despia uses the standard
        // WebAuthn path below (its Chromium WebView supports it natively).
        await triggerDespiaBioauth('Enable biometric unlock for your vault');
        const secret = generateDeviceSecret();
        await axios.post(
          `${API}/vault/bioauth/enroll`,
          { secret, device_label: '' },
          { withCredentials: true },
        );
        saveDeviceSecret(currentUserId, secret);
      } else {
        const pageOrigin = window.location.origin;
        const optRes = await axios.post(
          `${API}/vault/webauthn/register-options`,
          { origin: pageOrigin },
          { withCredentials: true },
        );
        const credential = await registerBiometric(optRes.data);
        await axios.post(
          `${API}/vault/webauthn/register`,
          { ...credential, origin: pageOrigin },
          { withCredentials: true },
        );
      }
      try {
        localStorage.setItem(DEVICE_ENROLLED_KEY(currentUserId), '1');
      } catch {}
      toast.success(t('generated.biometricdevicespanel_biometric_enabled_on_e2ea'));
      await fetchDevices();
      onChange?.();
    } catch (err) {
      const name = err?.name;
      const msg = String(err?.message || '');
      const serverMsg = err?.response?.data?.detail;
      console.error('[biometric] enroll failed', { name, msg, serverMsg, err });
      if (name === 'NotAllowedError' || msg.includes('cancelled')) {
        toast.error(t('generated.biometricdevicespanel_biometric_prompt_was_7bc0'));
      } else if (name === 'SecurityError') {
        toast.error(t('generated.biometricdevicespanel_security_error_site_e94b'));
      } else if (name === 'InvalidStateError') {
        toast.error(t('generated.biometricdevicespanel_this_device_is_ae91'));
      } else if (name === 'NotSupportedError') {
        toast.error(t('generated.biometricdevicespanel_your_device_does_2d1b'));
      } else if (name === 'AbortError') {
        toast.error(t('generated.biometricdevicespanel_biometric_prompt_timed_154c'));
      } else if (msg.includes('unavailable')) {
        toast.error(t('generated.biometricdevicespanel_biometric_is_not_ae68'));
      } else if (msg.includes('bridge_load_failed') || msg.includes('not_despia')) {
        toast.error(t('generated.biometricdevicespanel_biometric_bridge_unavailable_b390'));
      } else if (msg.includes('timeout')) {
        toast.error(t('generated.biometricdevicespanel_biometric_prompt_timed_154c'));
      } else if (serverMsg) {
        toast.error(`Server error: ${serverMsg}`);
      } else {
        toast.error(`Failed to enable biometric: ${msg || name || 'unknown error'}`);
      }
    } finally {
      setEnrolling(false);
    }
  };

  // Hidden if user has no PIN at all (vault not configured)
  if (!hasPin && !loading) return null;

  // ── Enrollment button visibility ───────────────────────────────────
  //
  // The localStorage flag `vault_device_biometric_<userId>` is set when
  // a credential is successfully created on THIS device. It can become
  // stale in several legit scenarios:
  //   • User cleared the credential from the OS settings
  //   • Server-side credential was deleted from another device
  //   • A prior enrollment attempt failed but the flag got set
  //   • App was reinstalled and localStorage migrated but the OS key
  //     pair was wiped
  //
  // We trust the SERVER's device list as the source of truth. The flag
  // is only valid when there IS at least one corresponding device on
  // the server. Otherwise it's stale and we surface the enrollment CTA
  // so the user is never locked out of biometric setup.
  const flagSaysEnrolled =
    currentUserId && (() => {
      try { return localStorage.getItem(DEVICE_ENROLLED_KEY(currentUserId)) === '1'; }
      catch { return false; }
    })();
  const thisDeviceEnrolled = flagSaysEnrolled && devices.length > 0;
  const showEnrollButton =
    !loading && hasPin && biometricCapable && !thisDeviceEnrolled;
  // Allow explicit re-enrollment even when the flag claims this device
  // is set up — useful when the OS-level credential has been wiped.
  const allowReEnroll = !loading && hasPin && biometricCapable && thisDeviceEnrolled;

  return (
    <CollapsibleSettingsCard
      storageKey="biometric_devices"
      tabId="account"
      searchTerms="biometric face id touch id fingerprint vault unlock webauthn passkey"
      glassCard={glassCard}
      testId="biometric-devices-panel"
      defaultOpen={false}
      icon={<Fingerprint className="h-[18px] w-[18px]" style={{ color: 'var(--theme-accent)' }} />}
      title={t('generated.biometricdevicespanel_biometric_devices_343f')}
      subtitle={
        devices.length > 0
          ? `${devices.length} ${devices.length === 1 ? 'device' : 'devices'} enrolled`
          : 'Enable Face ID, Touch ID, or fingerprint'
      }
    >
      <p className="text-xs mb-4" style={{ color: 'var(--theme-textSecondary)', lineHeight: 1.5 }}>
        {t('generated.biometricdevicespanel_each_device_that_2e18')}
      </p>
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--theme-accent)' }} />
        </div>
      ) : devices.length === 0 ? (
        <div
          className="rounded-xl p-4 text-xs"
          style={{
            background: 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            color: 'var(--theme-textSecondary)',
          }}
          data-testid="biometric-devices-empty"
        >
          {t('generated.biometricdevicespanel_no_biometric_devices_2137')}
        </div>
      ) : (
        <div className="space-y-2" data-testid="biometric-devices-list">
          {devices.map((device) => {
            const Icon = PLATFORM_ICON[device.platform] || Tablet;
            const isEditing = editingId === device.credential_id;
            const isSaving = savingId === device.credential_id;
            return (
              <div
                key={device.credential_id}
                className="rounded-xl p-3 flex items-center gap-3"
                style={{
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                }}
                data-testid={`biometric-device-${device.credential_id.slice(0, 12)}`}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: 'var(--glass-bg-active)' }}
                >
                  <Icon className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRename(device);
                        if (e.key === 'Escape') cancelRename();
                      }}
                      autoFocus
                      maxLength={60}
                      className="w-full rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid var(--glass-border)',
                        color: 'var(--theme-text)',
                        fontSize: '16px',
                      }}
                      data-testid="biometric-device-rename-input"
                    />
                  ) : (
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-text)' }}>
                        {device.device_label}
                      </p>
                      {thisDeviceEnrolled && (
                        // We can't perfectly identify which credential is "this device"
                        // server-side, so we surface a generic "This device" hint when
                        // localStorage indicates an enrollment exists. The user can
                        // still figure out which row to remove based on platform/date.
                        (null)
                      )}
                    </div>
                  )}
                  <p className="text-xs mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
                    {PLATFORM_LABEL[device.platform] || 'Device'} {t('generated.biometricdevicespanel_added_76b1')} {formatDate(device.created_at)}
                    {device.last_used_at ? ` · Last used ${formatRelative(device.last_used_at)}` : ''}
                  </p>
                </div>
                {isEditing ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => saveRename(device)}
                      disabled={isSaving}
                      data-testid="biometric-device-save-btn"
                    >
                      {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={cancelRename}
                      disabled={isSaving}
                      data-testid="biometric-device-cancel-btn"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => startRename(device)}
                      data-testid="biometric-device-rename-btn"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => removeDevice(device)}
                      disabled={isSaving}
                      data-testid="biometric-device-remove-btn"
                    >
                      {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Enroll-on-this-device CTA — shown when the server reports no
          credential for this device (we trust the server-side device
          list, NOT only the local flag, so stale localStorage state
          can never lock the user out of enrollment). */}
      {showEnrollButton && (
        <Button
          onClick={enrollThisDevice}
          disabled={enrolling}
          className="w-full mt-3 rounded-xl gap-2"
          style={{ background: 'var(--theme-accent)', color: '#fff' }}
          data-testid="biometric-enroll-this-device-btn"
        >
          {enrolling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {enrolling ? 'Enrolling…' : (devices.length === 0 ? 'Enable on this device' : 'Add this device')}
        </Button>
      )}
      {/* Re-enroll: when our local flag says this device is set up but
          the user can no longer unlock with biometric (OS wiped the
          credential, etc.), we surface an explicit way to redo it. */}
      {allowReEnroll && (
        <div className="mt-3 flex items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2" style={{ color: 'var(--theme-textSecondary)' }}>
            <ShieldCheck className="h-3.5 w-3.5" style={{ color: '#10b981' }} />
            <span>{t('generated.biometricdevicespanel_this_device_is_f1dd')}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              try { localStorage.removeItem(DEVICE_ENROLLED_KEY(currentUserId)); } catch {}
              enrollThisDevice();
            }}
            disabled={enrolling}
            className="underline disabled:opacity-50"
            style={{ color: 'var(--theme-accent)' }}
            data-testid="biometric-reenroll-this-device-btn"
          >
            {enrolling ? 'Re-enrolling…' : 'Re-enroll'}
          </button>
        </div>
      )}
    </CollapsibleSettingsCard>
  );
}
